import React, { useEffect, useMemo, useRef, useState, lazy, Suspense, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSort, faReceipt, faShoppingBag, faSearch, faClock, faCheckCircle, faTimes, faQrcode, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from '@tanstack/react-query';


const QRCode = lazy(() => import("qrcode.react"));

const API_BASE = "https://boxmealadminapinew.azurewebsites.net";

export default function PaymentHistory() {
    const location = useLocation();
    const navigate = useNavigate();
    const auth = useAuth();

    const userFromState = location.state?.user;
    const tokenFromState = location.state?.token;
    const user = userFromState ?? auth?.user;
    const token = tokenFromState ?? auth?.token;

    const authReady = tokenFromState !== undefined || auth?.token !== undefined;

    // UI State
    const [sortOrder, setSortOrder] = useState("desc");
    const [filterMonth, setFilterMonth] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [qrOpen, setQrOpen] = useState(false);
    const [qrValue, setQrValue] = useState("");
    // modern UI additions
    // const [copied, setCopied] = useState(false); // removed copy feature per request

    // Pagination stub (phase 2)
    const INITIAL_COUNT = 100;
    const PAGE_STEP = 100;
    const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

    const mountedRef = useRef(false);
    const redirectedRef = useRef(false);

    //helpers for swedish date time conversion from utc
    const STOCKHOLM_TZ = "Europe/Stockholm";

    const fmtDateSE = new Intl.DateTimeFormat("sv-SE", {
        timeZone: STOCKHOLM_TZ,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });

    const fmtTimeSE = new Intl.DateTimeFormat("sv-SE", {
        timeZone: STOCKHOLM_TZ,
        hour: "2-digit",
        minute: "2-digit",
    });

// Parse safely: if the string looks ISO without timezone (naive UTC), treat it as UTC by appending 'Z'
    const parseDateSafe = (val) => {
        if (!val) return new Date(NaN);
        const s = typeof val === "string" ? val : String(val);
        const looksISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s);
        const hasTZ = /[zZ]|[+-]\d{2}:\d{2}$/.test(s);
        return new Date(looksISO && !hasTZ ? `${s}Z` : s);
    };

// Use Stockholm time when formatting
    const formatDateTime = (dateTime) => {
        const d = parseDateSafe(dateTime);
        if (Number.isNaN(d.getTime())) return { date: "Ogiltigt datum", time: "" };
        return { date: fmtDateSE.format(d), time: fmtTimeSE.format(d) };
    };

// Get month index (0–11) in Stockholm time
    const monthInStockholm = useCallback((dateTime) => {
        const d = parseDateSafe(dateTime);
        if (Number.isNaN(d.getTime())) return NaN;
        // format month as 1–12, then convert to 0–11
        const m = new Intl.DateTimeFormat("sv-SE", {
            timeZone: STOCKHOLM_TZ,
            month: "numeric",
        }).format(d);
        return Number(m) - 1;
    }, []);

    const isIOS = useMemo(
        () => /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream,
        []
    );

    const log = (...a) => console.log("[PaymentHistory]", ...a);

    useEffect(() => {
        mountedRef.current = true;
        log("mounted");
        return () => { mountedRef.current = false; };
    }, []);

    // Redirect unauthenticated once auth resolution complete
    useEffect(() => {
        if (!authReady) return;
        if (!token && !redirectedRef.current) {
            redirectedRef.current = true;
            navigate("/", { replace: true });
        }
    }, [authReady, token, navigate]);

    // Fetch payment records via React Query
    const includeItems = true;
    const {
        data: records = [],
        isLoading,
        isError,
        error,
        refetch,
        isFetching,
    } = useQuery({
        queryKey: ['paymentRecords', user?.id, includeItems],
        enabled: !!(user?.id && token && authReady),
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            const url = `${API_BASE}/api/PaymentRecords/paid?userId=${encodeURIComponent(user.id)}&includeItems=${includeItems}`;
            log('fetching payment records', url);
            const resp = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json'
                }
            });
            if (!resp.ok) throw new Error(`Failed fetching payment records (${resp.status})`);
            const json = await resp.json();
            console.log("[PaymentHistory] fetched records:", json);
            return Array.isArray(json) ? json : [];
        },
        select: (arr) => {
            const paid = arr.filter(r => String(r?.status || '').toUpperCase() === 'PAID');
            return [...paid].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
        }
    });

    // Derived / filtered collection
    const filteredHistory = useMemo(() => {
        const res = records.filter(record => {
            if (!record) return false;
            const datePreferred = record.datePaid || record.dateCreated;
            const matchesMonth = !filterMonth || (!Number.isNaN(monthInStockholm(datePreferred)) && monthInStockholm(datePreferred) === Number(filterMonth));
            const matchesSearch = searchQuery ? (record.items || []).some(it => it?.productName && it.productName.toLowerCase().includes(searchQuery.toLowerCase())) : true;
            return matchesMonth && matchesSearch;
        });
        if (sortOrder === 'asc') return [...res].sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated));
        return res;
    }, [records, filterMonth, searchQuery, sortOrder, monthInStockholm]);

    const slicedHistory = useMemo(() => filteredHistory.slice(0, Math.min(visibleCount, filteredHistory.length)), [filteredHistory, visibleCount]);
    useEffect(() => { setVisibleCount(INITIAL_COUNT); }, [searchQuery, filterMonth, sortOrder, records.length]);

    const toggleSortOrder = () => setSortOrder(p => p === 'desc' ? 'asc' : 'desc');
    const formatCurrency = (amt) => new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(amt || 0));
    const getTimeAgo = (dateTime) => { const now = new Date(); const d = new Date(dateTime); if (Number.isNaN(d.getTime())) return "Okänt datum"; const diffH = Math.floor((now - d) / 3600000); if (diffH < 1) return "Nyss"; if (diffH < 24) return `${diffH}h sedan`; const diffD = Math.floor(diffH / 24); if (diffD < 30) return `${diffD} dagar sedan`; const diffM = Math.floor(diffD / 30); return `${diffM} månader sedan`; };
    const getItems = (r) => (Array.isArray(r?.items) ? r.items : []);
    const getDisplayDate = (r) => r?.datePaid || r?.dateCreated || null;
    const getQrString = (r) => { const val = r?.qrcode ?? r?.qrCode ?? r?.unlockCode ?? r?.code ?? r?.qr ?? r?.meta?.qr ?? ""; const s = String(val ?? "").trim(); return s && s !== 'null' && s !== 'undefined' ? s : ""; };

    const openQr = (record) => { const val = getQrString(record); setQrValue(val || ""); setQrOpen(true); };
    const closeQr = () => { setQrOpen(false); setQrValue(""); };
    const canLoadMore = visibleCount < filteredHistory.length;
    const handleSelectTransaction = (record) => { const items = getItems(record); if (items.length) { const computed = items.reduce((s, it) => s + (Number(it.quantity) * Number(it.price)), 0); if (Math.round(computed) !== Math.round(record.amount)) console.warn('Amount discrepancy', record.amount, computed); } setSelectedTransaction(record); };
    const deriveStatusInfo = (r) => { if (r.refundId) return { label: 'Återbetald', color: '#dc2626', bg: 'rgba(220,38,38,0.1)' }; const up = String(r.status || '').toUpperCase(); if (up === 'PAID') return { label: 'Betald', color: '#059669', bg: 'rgba(16,185,129,0.1)' }; return { label: 'Fel', color: '#b45309', bg: 'rgba(251,191,36,0.18)' }; };

    // Loading skeleton
    if (isLoading || (isFetching && records.length === 0)) {
        return (
            <Container $heavy={!isIOS || filteredHistory.length < 150}>
                <Header>
                    <BackButton onClick={() => navigate(-1)}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </BackButton>
                    <HeaderTitle>Betalningshistorik</HeaderTitle>
                    <HeaderActions>
                        <ActionButton disabled>
                            <FontAwesomeIcon icon={faSort} />
                        </ActionButton>
                    </HeaderActions>
                </Header>
                <ContentWrapper>
                    <SkeletonList>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <SkeletonCard key={i} />
                        ))}
                    </SkeletonList>
                </ContentWrapper>
            </Container>
        );
    }

    // Error state
    if (isError) {
        return (
            <Container>
                <Header>
                    <BackButton onClick={() => navigate(-1)}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </BackButton>
                    <HeaderTitle>Betalningshistorik</HeaderTitle>
                    <HeaderActions />
                </Header>
                <ErrorState>
                    <h3>Ett fel inträffade</h3>
                    <p>{error?.message || 'Kunde inte hämta betalningar.'}</p>
                    <RetryButton onClick={() => refetch()}>Försök igen</RetryButton>
                </ErrorState>
            </Container>
        );
    }

    return (
        <Container $heavy={!isIOS || filteredHistory.length < 150}>
            {/* Header */}
            <Header>
                <BackButton onClick={() => navigate(-1)}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                </BackButton>
                <HeaderTitle>Betalningshistorik</HeaderTitle>
                <HeaderActions>
                    <ActionButton onClick={toggleSortOrder} title="Sortera">
                        <FontAwesomeIcon icon={faSort} />
                    </ActionButton>
                </HeaderActions>
            </Header>

            <ContentWrapper>
                {/* Search & Month filter */}
                <FilterSection>
                    <SearchContainer>
                        <SearchIcon>
                            <FontAwesomeIcon icon={faSearch} />
                        </SearchIcon>
                        <SearchInput
                            type="text"
                            placeholder="Sök efter produkter..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <ClearButton onClick={() => setSearchQuery("")}> <FontAwesomeIcon icon={faTimes} /> </ClearButton>
                        )}
                    </SearchContainer>

                    <MonthFilter
                        value={filterMonth}
                        onChange={(e) => setFilterMonth(e.target.value)}
                        aria-label="Filtera på månad"
                    >
                        <option value="">Alla månader</option>
                        <option value="0">Januari</option>
                        <option value="1">Februari</option>
                        <option value="2">Mars</option>
                        <option value="3">April</option>
                        <option value="4">Maj</option>
                        <option value="5">Juni</option>
                        <option value="6">Juli</option>
                        <option value="7">Augusti</option>
                        <option value="8">September</option>
                        <option value="9">Oktober</option>
                        <option value="10">November</option>
                        <option value="11">December</option>
                    </MonthFilter>
                </FilterSection>

                {/* Transaction list */}
                <TransactionList>
                    {slicedHistory.length > 0 ? (
                        <>
                            {slicedHistory.map((record, idx) => {
                                const items = getItems(record);
                                const displayDate = getDisplayDate(record);
                                const { date, time } = formatDateTime(displayDate);
                                const statusInfo = deriveStatusInfo(record);
                                const fullName = [record.firstName, record.lastName].filter(Boolean).join(' ');
                                const orderRef = record.paymentReference || record.payeePaymentReference || '—';
                                return (
                                    <TransactionCard
                                        key={`${record?.paymentRequestId ?? record?.dateCreated ?? idx}-${idx}`}
                                        $heavy={!isIOS || filteredHistory.length < 150}
                                        onClick={() => handleSelectTransaction(record)}
                                    >
                                        <TransactionHeader>
                                            <TransactionDate>
                                                <DateMain>{date}</DateMain>
                                                <TimeInfo>{time}</TimeInfo>
                                            </TransactionDate>
                                            <TransactionAmount>
                                                {formatCurrency(record.amount)} {record.currency || 'SEK'}
                                            </TransactionAmount>
                                        </TransactionHeader>

                                        <TransactionMeta>
                                            <TimeAgo title={displayDate}>
                                                <FontAwesomeIcon icon={faClock} />
                                                {getTimeAgo(displayDate)}
                                            </TimeAgo>
                                            <ItemCount>
                                                <FontAwesomeIcon icon={faShoppingBag} />
                                                {items.length} produkter
                                            </ItemCount>
                                            <StatusBadge $color={statusInfo.color} $bg={statusInfo.bg} title={record.errorMessage || ''}>
                                                <FontAwesomeIcon icon={faCheckCircle} />
                                                {statusInfo.label}
                                            </StatusBadge>
                                        </TransactionMeta>

                                        <OrderRefRow>
                                            <OrderRefLabel>Referens:</OrderRefLabel>
                                            <OrderRefValue>{orderRef}</OrderRefValue>
                                        </OrderRefRow>
                                        {fullName && (
                                            <OrderRefRow>
                                                <OrderRefLabel>Kund:</OrderRefLabel>
                                                <OrderRefValue>{fullName}</OrderRefValue>
                                            </OrderRefRow>
                                        )}

                                        <CardActionsRow>
                                            <QrIconButton
                                                title="Visa QR-kod"
                                                aria-label="Visa QR-kod"
                                                data-qr-present={Boolean(getQrString(record))}
                                                onClick={(e) => { e.stopPropagation(); openQr(record); }}
                                            >
                                                <FontAwesomeIcon icon={faQrcode} />
                                            </QrIconButton>
                                        </CardActionsRow>
                                    </TransactionCard>
                                );
                            })}

                            {canLoadMore && (
                                <LoadMoreWrap>
                                    <LoadMoreButton
                                        onClick={() => {
                                            const next = Math.min(visibleCount + PAGE_STEP, filteredHistory.length);
                                            setVisibleCount(next);
                                        }}
                                    >
                                        Visa fler ({filteredHistory.length - visibleCount})
                                    </LoadMoreButton>
                                </LoadMoreWrap>
                            )}
                        </>
                    ) : (
                        <EmptyState>
                            <EmptyIcon>
                                <FontAwesomeIcon icon={faReceipt} />
                            </EmptyIcon>
                            <EmptyTitle>Inga betalningar hittades</EmptyTitle>
                            <EmptyText>
                                {searchQuery || filterMonth ? 'Prova att ändra dina filterinställningar' : 'Det finns inga betalningar ännu'}
                            </EmptyText>
                        </EmptyState>
                    )}
                </TransactionList>
            </ContentWrapper>

            {/* Details modal */}
            {selectedTransaction && (
                <ModalOverlay onClick={() => setSelectedTransaction(null)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>Betalningsdetaljer</ModalTitle>
                            <CloseButton onClick={() => setSelectedTransaction(null)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </CloseButton>
                        </ModalHeader>

                        <ModalBody>
                            <TransactionDetailHeader>
                                <DetailAmount>
                                    {formatCurrency(selectedTransaction.amount)} {selectedTransaction.currency || 'SEK'}
                                </DetailAmount>
                                <DetailDate>
                                    {formatDateTime(getDisplayDate(selectedTransaction)).date} • {formatDateTime(getDisplayDate(selectedTransaction)).time}
                                </DetailDate>
                                <DetailMetaRow>
                                    <MetaLabel>Status:</MetaLabel>
                                    <MetaValue>{deriveStatusInfo(selectedTransaction).label}</MetaValue>
                                </DetailMetaRow>
                                <DetailMetaRow>
                                    <MetaLabel>Referens:</MetaLabel>
                                    <MetaValue>{selectedTransaction.paymentReference || selectedTransaction.payeePaymentReference || '—'}</MetaValue>
                                </DetailMetaRow>
                                {(selectedTransaction.firstName || selectedTransaction.lastName) && (
                                    <DetailMetaRow>
                                        <MetaLabel>Kund:</MetaLabel>
                                        <MetaValue>{[selectedTransaction.firstName, selectedTransaction.lastName].filter(Boolean).join(' ')}</MetaValue>
                                    </DetailMetaRow>
                                )}
                                {selectedTransaction.errorMessage && (
                                    <DetailMetaRow>
                                        <MetaLabel>Fel:</MetaLabel>
                                        <MetaValue>{selectedTransaction.errorMessage}</MetaValue>
                                    </DetailMetaRow>
                                )}
                            </TransactionDetailHeader>

                            <ProductDetailsList>
                                <ProductsTitle>
                                    Produkter {getItems(selectedTransaction).length ? `(${getItems(selectedTransaction).length})` : ''}
                                </ProductsTitle>
                                {getItems(selectedTransaction).length ? (
                                    getItems(selectedTransaction).map((item, i) => (
                                        <ProductDetailItem key={i}>
                                            <ProductDetailInfo>
                                                <ProductDetailName>{item?.productName || 'Okänd produkt'}</ProductDetailName>
                                                <ProductDetailMeta>
                                                    {item?.quantity && item?.price ? `${item.quantity} st × ${formatCurrency(item.price)} ${selectedTransaction.currency || 'SEK'}` : 'Pris ej tillgängligt'}
                                                </ProductDetailMeta>
                                            </ProductDetailInfo>
                                            <ProductDetailTotal>
                                                {item?.quantity && item?.price ? `${formatCurrency(item.quantity * item.price)} ${selectedTransaction.currency || 'SEK'}` : `0 ${selectedTransaction.currency || 'SEK'}`}
                                            </ProductDetailTotal>
                                        </ProductDetailItem>
                                    ))
                                ) : (
                                    <p>Inga produkter hittades</p>
                                )}
                            </ProductDetailsList>
                        </ModalBody>

                        <ModalFooter />
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* QR Modal */}
            {qrOpen && (
                <ModalOverlay onClick={closeQr}>
                    <ModalContent $qr onClick={(e) => e.stopPropagation()}>
                        <FloatingElement $top="8%" $left="10%">✨</FloatingElement>
                        <FloatingElement $top="22%" $right="12%">🎉</FloatingElement>
                        <FloatingElement $bottom="20%" $left="16%">⭐</FloatingElement>
                        <FloatingElement $bottom="8%" $right="14%">💫</FloatingElement>
                        <ModalHeader $qr>
                            <ModalTitle>QR-kod</ModalTitle>
                            <CloseButton onClick={closeQr}>
                                <FontAwesomeIcon icon={faTimes} />
                            </CloseButton>
                        </ModalHeader>
                        <ModalBody $qr style={{ display: 'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:24 }}>
                            <Suspense fallback={<QrFallback><FontAwesomeIcon icon={faSpinner} spin /> Laddar...</QrFallback>}>
                                {qrValue && (
                                    <QRSection>
                                        <QRWrapper>
                                            <QRCode value={qrValue} size={220} bgColor="#ffffff" fgColor="#000000" includeMargin renderAs="svg" />
                                            <QRGlow />
                                        </QRWrapper>
                                        <InstructionCard>
                                            <InstructionIcon>📱</InstructionIcon>
                                            <InstructionText>Rikta koden mot kameran på enheten</InstructionText>
                                        </InstructionCard>
                                    </QRSection>
                                )}
                            </Suspense>
                        </ModalBody>
                    </ModalContent>
                </ModalOverlay>
            )}
        </Container>
    );
}

/* ================ Animations ================ */
const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
`;
const slideInRight = keyframes`
    from { opacity: 0; transform: translateX(30px); }
    to { opacity: 1; transform: translateX(0); }
`;
const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;
const gradientShift = keyframes`
    0% { background-position: 0 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
`;
const fadeInScale = keyframes`
    from { opacity: 0; transform: scale(.8) translateY(30px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
`;
const pulse = keyframes`
    0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(63,196,138,.35); }
    50% { transform: scale(1.02); box-shadow: 0 0 0 10px rgba(63,196,138,0); }
`;
const qrGlowAnim = keyframes`
    0%,100% { box-shadow: 0 0 20px rgba(63,196,138,.3); }
    50% { box-shadow: 0 0 36px rgba(63,196,138,.55); }
`;
const float = keyframes`
    0%,100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
`;
const sparkle = keyframes`
    0%,100% { opacity: 0; transform: scale(0) rotate(0deg); }
    50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

/* ================ Styled Components ================ */
const Container = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #f6f7fa 0%, #e9ecef 100%);
    position: relative;
`;

const Header = styled.div`
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px;
    background: ${({$heavy}) => $heavy ? "rgba(255,255,255,0.9)" : "#fff"};
    position: sticky; top: 0; z-index: 100;
`;
const BackButton = styled.button`
    width: 44px; height: 44px; border-radius: 12px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.8);
    color: #374151; display: flex; align-items: center; justify-content: center;
    cursor: pointer;
`;
const HeaderTitle = styled.h1` font-size: 20px; font-weight: 700; color: #1f2937; margin: 0; `;
const HeaderActions = styled.div` display: flex; gap: 8px; `;
const ActionButton = styled.button`
    width: 44px; height: 44px; border-radius: 12px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.8);
    color: #374151; display: flex; align-items: center; justify-content: center;
    cursor: pointer;
`;

const ContentWrapper = styled.div`
    padding: 20px; max-width: 600px; margin: 0 auto; animation: ${fadeInUp} 0.4s ease-out;
    min-height: 100vh;
`;

const FilterSection = styled.div` display: flex; gap: 12px; margin-bottom: 24px; `;
const SearchContainer = styled.div` flex: 1; position: relative; `;
const SearchIcon = styled.div`
    position: absolute; left: 16px; top: 50%; transform: translateY(-50%);
    color: #9ca3af; font-size: 16px; pointer-events: none;
`;
const SearchInput = styled.input`
    width: 100%;
    height: 48px; 
    padding: 0 48px; 
    border: 1px solid rgba(255,255,255,0.8);
    border-radius: 12px; 
    background: rgba(255,255,255,0.9);
    font-size: 16px; 
    color: #1f2937; 
    transition: all 0.2s ease;
    &:focus 
    { 
        outline: none; 
        border-color: #252525; 
        box-shadow: 0 0 0 3px rgba(59,130,246,0.1); 
        background: #fff; }
    &::placeholder 
    { 
        color: #9ca3af; 
    }
`;
const ClearButton = styled.button`
    position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
    width: 24px; height: 24px; border: none; background: #f3f4f6; border-radius: 50%;
    color: #6b7280; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 12px;
`;

const MonthFilter = styled.select`
    height: 48px; padding: 0 16px; border: 1px solid rgba(255,255,255,0.8); border-radius: 12px;
    background: rgba(255,255,255,0.9); font-size: 14px; color: #1f2937; cursor: pointer; min-width: 140px;
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat; background-position: right 12px center; background-size: 16px; padding-right: 40px;
`;

const TransactionList = styled.div` display: flex; flex-direction: column; gap: 16px; padding-bottom: 100px; `;

const slideMaybe = css` animation: ${slideInRight} 0.3s ease-out; `;

const TransactionCard = styled.div`
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: 20px; padding: 20px; cursor: pointer; position: relative; overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    ${({$heavy}) => $heavy && slideMaybe}
    &:hover 
    { 
        transform: translateY(-2px); 
        box-shadow: 0 8px 18px rgba(0,0,0,0.12); 
        border-color: #252525; }
`;

const TransactionHeader = styled.div` display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; `;
const TransactionDate = styled.div``;
const DateMain = styled.div` font-size: 16px; font-weight: 600; color: #1f2937; `;
const TimeInfo = styled.div` font-size: 14px; color: #6b7280; margin-top: 2px; `;
const TransactionAmount = styled.div`
    font-size: 18px; font-weight: 700; color: #059669; background: rgba(16,185,129,0.1);
    padding: 4px 10px; border-radius: 8px;
`;

const TransactionMeta = styled.div` display: flex; gap: 16px; margin-bottom: 12px; flex-wrap: wrap; `;
const TimeAgo = styled.div` display: flex; align-items: center; gap: 6px; font-size: 12px; color: #6b7280; `;
const ItemCount = styled(TimeAgo)``;
const CardActionsRow = styled.div` 
    display: flex; 
    align-items: center; 
    justify-content: space-between; `;

const QrIconButton = styled.button`
    position: absolute;
    bottom: 12px;
    right: 12px;
    width: 42px;
    height: 42px;
    border: none;
    background: rgba(0,0,0,0.05);
    color: #252525;
    border-radius: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    &:hover { background: rgba(0,0,0,0.09); }
    &:active { transform: scale(.95); }
`;
const EmptyState = styled.div` text-align: center; padding: 60px 20px; `;
const EmptyIcon = styled.div` font-size: 48px; color: #e5e7eb; margin-bottom: 16px; `;
const EmptyTitle = styled.h3` font-size: 18px; font-weight: 600; color: #1f2937; margin: 0 0 8px 0; `;
const EmptyText = styled.p` font-size: 14px; color: #6b7280; margin: 0; line-height: 1.5; `;
const LoadMoreWrap = styled.div` display: flex; justify-content: center; margin-top: 8px; `;
const LoadMoreButton = styled.button`
    height: 44px; padding: 0 16px; border: none; border-radius: 12px; cursor: pointer;
    background: #1d4ed8; color: #fff; font-weight: 600;
`;
/* Modal Styles */
const ModalOverlay = styled.div`
    position: fixed; inset: 0; background: rgba(15,16,19,0.55);
    backdrop-filter: blur(10px) saturate(140%);
    -webkit-backdrop-filter: blur(10px) saturate(140%);
    display: flex; align-items: center; justify-content: center; z-index: 1000;
    animation: ${fadeInUp} 0.25s ease-out;
`;
const ModalContent = styled.div`
    background: rgba(255, 255, 255, 0.98);
    border-radius: 20px; max-width: 500px; width: 90%; max-height: 80vh; overflow: hidden;
    box-shadow: 0 20px 40px -8px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.4) inset;
    position: relative;
    ${({$qr}) => $qr && css`
        background: linear-gradient(135deg,#ffffff 0%,#f8fafc 50%,#ffffff 100%);
        background-size: 400% 400%;
        animation: ${gradientShift} 12s ease-in-out infinite;
        border: 1px solid rgba(255,255,255,0.6);
    `}
`;
const ModalHeader = styled.div`
    display: flex; justify-content: space-between; align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(229,231,235,0.6);
    ${({$qr}) => $qr && css`
        background: linear-gradient(120deg,#111827 0%,#1f2937 60%,#374151 100%);
        color:#f9fafb;
        border-bottom: 1px solid rgba(255,255,255,0.12);
    `}
`;
const ModalTitle = styled.h2` font-size: 18px; font-weight: 600; color: #ff; margin: 0; `;
const CloseButton = styled.button`
    width: 32px; height: 32px; border-radius: 8px; background: transparent; border: none; color: #6b7280; cursor: pointer;
`;
const ModalBody = styled.div`
    padding: 20px; max-height: 60vh; overflow-y: auto;
    ${({$qr}) => $qr && css`
        background: radial-gradient(circle at 50% 0%, rgba(59,130,246,0.08) 0%, rgba(59,130,246,0) 60%);
    `}
`;
const ModalFooter = styled.div` padding: 16px 20px; border-top: 1px solid rgba(229,231,235,0.6); `;

const TransactionDetailHeader = styled.div`
    text-align: center; margin-bottom: 16px; padding: 16px;
    background: rgba(59,130,246,0.06);
    border-radius: 12px;
`;
const DetailAmount = styled.div` font-size: 28px; font-weight: 700; color: #059669; margin-bottom: 6px; `;
const DetailDate = styled.div` font-size: 14px; color: #6b7280; `;
const ProductDetailsList = styled.div``;
const ProductsTitle = styled.h3` font-size: 16px; font-weight: 600; color: #1f2937; margin: 0 0 12px; `;
const ProductDetailItem = styled.div` display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid rgba(229,231,235,0.3); &:last-child { border-bottom: none; }`;
const ProductDetailInfo = styled.div` flex: 1; `;
const ProductDetailName = styled.div` font-size: 14px; font-weight: 500; color: #1f2937; margin-bottom: 4px; `;
const ProductDetailMeta = styled.div` font-size: 12px; color: #6b7280; `;
const ProductDetailTotal = styled.div` font-size: 14px; font-weight: 600; color: #374151; `;

/* Additional Styled Components for new states */
const SkeletonList = styled.div` display: flex; flex-direction: column; gap: 16px; margin-top: 12px;`;
const SkeletonCard = styled.div` height: 120px; border-radius: 20px; position: relative; overflow: hidden; background: linear-gradient(90deg,#f3f4f6 0%,#e5e7eb 50%,#f3f4f6 100%); background-size:200% 100%; animation: ${shimmer} 1.2s linear infinite; border: 1px solid rgba(255,255,255,0.8);
`;
const StatusBadge = styled.div`
    display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: ${({$color}) => $color}; background: ${({$bg}) => $bg}; padding: 4px 8px; border-radius: 8px;
`;
const OrderRefRow = styled.div` display: flex; gap: 8px; font-size: 12px; color: #374151; margin: 2px 0;`;
const OrderRefLabel = styled.div` font-weight: 600; color: #6b7280;`;
const OrderRefValue = styled.div` flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const ErrorState = styled.div` text-align: center; padding: 80px 20px;`;
const RetryButton = styled.button` margin-top: 16px; background:#1d4ed8; color:#fff; border:none; padding:10px 18px; border-radius:12px; cursor:pointer; font-weight:600;`;
const DetailMetaRow = styled.div` display:flex; justify-content:center; gap:8px; font-size:12px; color:#374151; flex-wrap:wrap; margin:2px 0;`;
const MetaLabel = styled.span` font-weight:600; color:#6b7280;`;
const MetaValue = styled.span``;
const QrFallback = styled.div` padding: 24px; display:flex; align-items:center; gap:10px; font-size:14px; color:#374151; font-weight:500; `;

/* QR UI */
const QRSection = styled.div` display: flex; flex-direction: column; align-items: center; gap: 18px; `;
const QRWrapper = styled.div`
    position: relative;
    background: #ffffff;
    padding: 24px;
    border-radius: 20px;
    box-shadow: 0 8px 25px rgba(0,0,0,0.1);
    animation: ${fadeInScale} .8s cubic-bezier(.4,0,.2,1), ${pulse} 2s ease-in-out;
    display: flex; align-items: center; justify-content: center;
`;
const QRGlow = styled.div`
    position: absolute; top: -4px; left: -4px; right: -4px; bottom: -4px;
    background: linear-gradient(45deg,#10b981,#3b82f6,#8b5cf6,#10b981);
    border-radius: 24px; z-index: -1; opacity: .4;
    animation: ${sparkle} 4s ease-in-out infinite, ${qrGlowAnim} 3s ease-in-out infinite;
    filter: blur(4px);
`;
const InstructionCard = styled.div`
    display: flex; align-items: center; gap: 12px;
    background: rgba(59,130,246,0.1);
    padding: 14px 18px;
    border-radius: 12px;
    border: 1px solid rgba(59,130,246,0.2);
    max-width: 320px;
`;
const InstructionIcon = styled.div` font-size: 22px; `;
const InstructionText = styled.div` font-size: 13px; color: #1e40af; font-weight: 500; line-height: 1.35; text-align: center; `;
const FloatingElement = styled.div`
    position: absolute; font-size: 22px; animation: ${float} 3.4s ease-in-out infinite; opacity: .75; z-index: 2;
    ${({$top}) => $top && css` top: ${$top}; `}
    ${({$left}) => $left && css` left: ${$left}; `}
    ${({$right}) => $right && css` right: ${$right}; `}
    ${({$bottom}) => $bottom && css` bottom: ${$bottom}; `}
`;
